import {
    serializable,
    DynamicFormControlLayout,
    DynamicInputModel,
    DynamicInputModelConfig,
    MATCH_VISIBLE,
} from "@ng-dynamic-forms/core";
import { Model_WorkflowComponent, CODE_WORKFLOW_COMPONENT, TYPE_DATASOURCE, TYPE_DEFAULT_VALUE, TYPE_WORKFLOW_COMPONENT_OBSERVER, EXPRESSION_FIELD_SCOPE, FONCTION_WORKFLOW_COMPONENT_OPTIONS } from 'src/app/app.models';
import { COMPONENT_MODE, COMPONENT_UI } from '../workflow-components.models';
import { WorkflowComponentBuilderOptions } from '../workflow-components.service';
  
export interface KanbanModelConfig extends DynamicInputModelConfig {
    componentMode: COMPONENT_MODE;
	responseWorkflowLocalId: string;
}

export enum KANBAN_TYPE_TASK {
    DEPOT_CHEQUE = 'DEPOT_CHEQUE',
    RETRAIT_CHEQUE = 'RETRAIT_CHEQUE',
    DEPOT_FACTURE = 'DEPOT_FACTURE'
}

export enum KANBAN_TYPE_FACTURATION {
	FACTURATION_SPONTANEE = 'FACTURATION_SPONTANEE',
	FACTURATION_MENSUELLE = 'FACTURATION_MENSUELLE'
}
  
export class KanbanModel extends DynamicInputModel {
    @serializable() readonly type: string = 'kanban'; 
    componentMode: COMPONENT_MODE;
	responseWorkflowLocalId: string;

    constructor(config: KanbanModelConfig, layout?: DynamicFormControlLayout) {
        super(config, layout);
        this.componentMode = config.componentMode;
		this.responseWorkflowLocalId = config.responseWorkflowLocalId;
    }

    static initModel(_component: Model_WorkflowComponent, _options: WorkflowComponentBuilderOptions): KanbanModel {
        const formModel = new KanbanModel({
            id: _component.ID,
            name: _component.ID,
            label: _component.LABEL,
            value: _component.DEFAULT_VALUE,
            required: _component.IS_REQUIRED,
            readOnly: _component.IS_READONLY,
            hidden: _component.IS_VISIBLE !== undefined ? _component.IS_VISIBLE === false : false,
            validators: _component.CUSTOM_VALIDATORS,
            componentMode: _component.COMPONENT_MODE,
            additional: _component,
            relations: _component.RELATIONS,
			responseWorkflowLocalId: _options.responseWorkflowLocalId
        });
        return formModel;
    }

    static getProperties(_options?: WorkflowComponentBuilderOptions): Model_WorkflowComponent {
        return {
            ID: 'kanban',
            CODE: CODE_WORKFLOW_COMPONENT.KANBAN,
            ICON: 'create-outline',
            LABEL: 'Kanban',
			HAS_DATASOURCE: true,
			DATASOURCES_INFOS: [
				{
					DS_PROP_ID: 'NUM_DATASOURCE',
					DS_PROP_VALUE_COLUMN_ID: 'VALUE_COLUMN_NAME',
					DS_PROP_DISPLAY_COLUMN_ID: 'DISPLAY_COLUMN_NAME',
					DS_BUILD_NAME: 'DATASOURCE',
				},
				{
					DS_PROP_ID: 'NUM_DATASOURCE_CLIENTS',
					DS_PROP_VALUE_COLUMN_ID: 'VALUE_COLUMN_NAME_CLIENTS',
					DS_PROP_DISPLAY_COLUMN_ID: 'DISPLAY_COLUMN_NAME_CLIENTS',
					DS_BUILD_NAME: 'DATASOURCE_CLIENTS',
				},
				{
					DS_PROP_ID: 'NUM_DATASOURCE_BANQUES',
					DS_PROP_VALUE_COLUMN_ID: 'VALUE_COLUMN_NAME_BANQUES',
					DS_PROP_DISPLAY_COLUMN_ID: 'DISPLAY_COLUMN_NAME_BANQUES',
					DS_BUILD_NAME: 'DATASOURCE_BANQUES',
				},
				{
					DS_PROP_ID: 'NUM_DATASOURCE_CHEQUES',
					DS_PROP_VALUE_COLUMN_ID: 'VALUE_COLUMN_NAME_CHEQUES',
					DS_PROP_DISPLAY_COLUMN_ID: 'DISPLAY_COLUMN_NAME_CHEQUES',
					DS_BUILD_NAME: 'DATASOURCE_CHEQUES',
				},
				{
					DS_PROP_ID: 'NUM_DATASOURCE_FACTURES',
					DS_PROP_VALUE_COLUMN_ID: 'VALUE_COLUMN_NAME_FACTURES',
					DS_PROP_DISPLAY_COLUMN_ID: 'DISPLAY_COLUMN_NAME_FACTURES',
					DS_BUILD_NAME: 'DATASOURCE_FACTURES',
				},
				{
					DS_PROP_ID: 'NUM_DATASOURCE_VALUES',
					DS_BUILD_NAME: 'NUM_DATASOURCE_VALUES'
				}
			],
            PROPERTIES: [
                { ID: 'GUID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'GUID', IS_VISIBLE: false },
                { ID: 'INDEX', CODE: CODE_WORKFLOW_COMPONENT.NUMERIC_TEXTBOX, LABEL: 'Index', DEFAULT_VALUE: 1 },
                { ID: 'ID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Identifiant', IS_REQUIRED: true, CUSTOM_VALIDATORS: [{ VALIDATOR_NAME: 'no_whitespace' }] },
                { ID: 'LABEL', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Libellé', IS_REQUIRED: true },
                { ID: 'IS_VISIBLE', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Visible ?', DEFAULT_VALUE: true },
                { ID: 'IS_REQUIRED', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Requis ?' },
                { ID: 'IS_READONLY', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Lecture Seule ?' },

                { ID: 'NUM_DATASOURCE', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Elements', IS_REQUIRED: true },
				{ ID: 'VALUE_COLUMN_NAME', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Valeur', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'DISPLAY_COLUMN_NAME', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Affichage', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},

                { ID: 'NUM_DATASOURCE_CLIENTS', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Clients', IS_REQUIRED: true },
				{ ID: 'VALUE_COLUMN_NAME_CLIENTS', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Valeur', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_CLIENTS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'DISPLAY_COLUMN_NAME_CLIENTS', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Affichage', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_CLIENTS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},

                { ID: 'NUM_DATASOURCE_BANQUES', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Banques', IS_REQUIRED: true },
				{ ID: 'VALUE_COLUMN_NAME_BANQUES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Valeur', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_BANQUES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'DISPLAY_COLUMN_NAME_BANQUES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Affichage', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_BANQUES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},

                { ID: 'NUM_DATASOURCE_CHEQUES', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Chèques', IS_REQUIRED: true },
				{ ID: 'ROWID_COLUMN_NAME_CHEQUES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Identifiant Ligne', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_CHEQUES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'VALUE_COLUMN_NAME_CHEQUES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Num Chèques', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_CHEQUES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'DISPLAY_COLUMN_NAME_CHEQUES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Montant Chèques', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_CHEQUES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},

				
                { ID: 'NUM_DATASOURCE_FACTURES', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Factures', IS_REQUIRED: true },
				{ ID: 'VALUE_COLUMN_NAME_FACTURES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Num Factures', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_FACTURES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'DISPLAY_COLUMN_NAME_FACTURES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Libellé Factures', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_FACTURES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'TOTALTTC_COLUMN_NAME_FACTURES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Total TTC Factures', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_FACTURES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'DO_TIERS_COLUMN_NAME_FACTURES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Identifiant Tiers', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_FACTURES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'LIB_TIERS_COLUMN_NAME_FACTURES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Libellé Tiers', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_FACTURES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},
				{ ID: 'ZONES_COLUMN_NAME_FACTURES', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Colonne Zone', IS_REQUIRED: true, 
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'NUM_DATASOURCE_FACTURES', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
						],
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [],
							DATASOURCE_VALUE_FIELD: 'value',
							DATASOURCE_DISPLAY_FIELD: 'display'
						}
					}
				},

				{ ID: 'NUM_DATASOURCE_VALUES', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Source de données Valeurs', HAS_DATASOURCE_FILTERS: false, HAS_CACHE_OPTION: false },

            ]
        };
    }
}
  